import { FC, useEffect, useState } from 'react';
import { Button, PrimaryButton, Input, Checkbox, InputPhoneInt, DatePicker, TextArea } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { styled } from '@mui/system';
import { COLORS } from 'src/constants';

import { Dropdown, Typography } from 'src/components';
import { useAuth } from 'src/contexts';
import { IAddress, IFormErrors, ValidationError, reduceAddress, IFieldErrors, IPhone} from 'src/types';
import { OrgServiceFactory, IAIUpdate, IAIUpdateRequest, getAIStatusOptions, getGradeName, getGradeOptions, getOrgSubTypeOptions, getOrgSubTypeLabel, isAIActive } from 'src/types/org';
import { TYPE_CODE_K12, TYPE_CODE_OTHER_ED } from 'src/types/org/constants';
import { get } from 'lodash';

import { AddressFields } from 'src/generic/component/address';
import { IRequestMeta } from 'src/types/org/request';

const StyledGrid = styled(Grid)`
  padding: 20px 0;
  border-bottom: 1px solid ${COLORS.borderNavy};

  &:first-of-type {
    border-top: 1px solid ${COLORS.borderNavy};
  }
`;

const orgService = new OrgServiceFactory();

interface FormProps {
  request: Partial<IAIUpdateRequest>;
  readOnly?: boolean;
  onComplete?: (update: IAIUpdateRequest) => void;
  onCancel?: () => void;
}

const FieldRow = styled(Grid)`
  margin-bottom:10px;
`;

const FieldData = styled(Grid)`
  margin-bottom:2px;
`;

interface IChildProp {
  children?: React.ReactNode;
}

const FieldValue: FC<IChildProp> = ({ children }) => {
  return <span style={{ fontWeight: '500' }}>{children}</span>;
};

const getInitial = (update: Partial<IAIUpdate>): Partial<IAIUpdate> => {
  const visiting = { ...update.visitingAddress };
  return {
    orgId: update.orgId,
    name: update.name,
    email: update.email,
    website: update.website,
    phone: {
      formatted: update.phone?.formatted
    },
    isMerge: update.isMerge || false,
    merge: update.merge,
    visitingAddress: reduceAddress(visiting),
    shippingAddress: update.shippingAddress ? reduceAddress(update.shippingAddress) : undefined,
    lowestGradeCd: update.lowestGradeCd,
    highestGradeCd: update.highestGradeCd,
    aiCode: update.aiCode,
    aiLevelCd: update.aiLevelCd,
    aiStatusCd: update.aiStatusCd,
    aiCategoryCd: update.aiCategoryCd,
    orgSubTypeCd: update.orgSubTypeCd,
    closureDate: update.closureDate,
    submitterComment: '',
    fax: {
      formatted: update.fax?.formatted
    }
  };
};

const isFieldSet = (field: string, data: any): boolean => {
  const path = field.split('.');
  let existing: any = data || {};
  for (const fragment of path) {
    if (fragment in existing) {
      existing = get(existing, fragment);
    } else {
      return false;
    }
  }
  return true;
};

const extractCountry = (phone: Partial<IPhone>) => {
  const cc = phone.phoneCc;
  const formatted = phone.formatted;
  if (cc && cc !== '') return cc;

  if (formatted) {
    const parts = formatted.split(' ');
    if (parts[0].includes('+') && parts[0].length <= 3) {
      return parts[0].replace('+', '');
    }
  }

  return 'US';
};

export const UpdateForm = ({ readOnly, request, onComplete, onCancel }: FormProps) => {
  const { data, original, changes, meta } = request;
  const update: Partial<IAIUpdate> = data || {};
  const initial = original || getInitial(update);

  const { userName, userEmail } = useAuth();

  let defaultCountry = update.phone ? extractCountry(update.phone) : undefined;
  let formattedPhone = update.phone ? (update.phone.formatted || '') : '';
  let formattedFax = update.fax ? (update.fax.formatted || '') : '';
  const orgTypeCd = update.orgTypeCd;
  const gradeRequired = [TYPE_CODE_K12, TYPE_CODE_OTHER_ED].includes(orgTypeCd || 0);

  const visiting = { ...update.visitingAddress };

  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [name, setName] = useState(update.name || '');
  const [email, setEmail] = useState(update.email || '');
  const [phone, setPhone] = useState(formattedPhone);
  const [fax, setFax] = useState(formattedFax);
  const [website, setWebsite] = useState(update.website || '');

  const [visitingAddress, setVisitingAddress] = useState<Partial<IAddress>>(visiting);
  const [hasShipping, setShipping] = useState(update.shippingAddress ? true : false);
  const [shippingAddress, setShippingAddress] = useState<Partial<IAddress> | undefined>(update.shippingAddress);

  const [aiStatusCd, setStatusCd] = useState<"1" | "2" | undefined>(update.aiStatusCd);
  const [closureDate, setClosureDate] = useState<string | undefined>(update.closureDate);
  const [isMerge, setMerge] = useState<boolean>(update.isMerge || false);
  const [mergeAICd, setMergeAICd] = useState<string>(update.merge?.aiCode || '');
  const [mergeName, setMergeName] = useState<string>(update.merge?.name || '');
  const [mergeAddress, setMergeAddress] = useState<Partial<IAddress> | undefined>(update.merge?.address);

  const [lowestGradeCd, setLowestGradeCd] = useState<string | undefined>(update.lowestGradeCd || undefined);
  const [highestGradeCd, setHighestGradeCd] = useState<string | undefined>(update.highestGradeCd || undefined);
  const [orgSubTypeCd, setOrgSubTypeCd] = useState<string | undefined>(update.orgSubTypeCd);
  const [disclaimer, setDisclaimer] = useState<boolean>(meta?.disclaimer || false);
  const [submitter, setSubmitter] = useState<string>(meta?.submitter || '');
  const [submitterComment, setSubmitterComment] = useState<string>(update.submitterComment || '');
  const [deactivateDisclaimer, setDeactivateDisclaimer] = useState<boolean | undefined>(update.deactivateDisclaimer);
  const [aiCategoryCd, setAiCategoryCd] = useState<string>(update.aiCategoryCd || '');

  // Error Handling
  const [error, setError] = useState<IFormErrors>({
    message: '',
    errors: {
      _errors: []
    }
  });

  const getData = (): Partial<IAIUpdate> => {
    const hasMergeName = mergeName.length > 0;
    const hasMergeCode = mergeAICd.length > 0;

    const merge = isMerge ? {
      name: hasMergeName ? mergeName : undefined,
      address: hasMergeName && mergeAddress ? reduceAddress(mergeAddress) : undefined,
      aiCode: hasMergeCode ? mergeAICd : undefined
    } : undefined;

    return {
      orgId: update.orgId,
      name,
      email,
      website,
      phone: {
        formatted: phone
      },
      isMerge,
      merge,
      visitingAddress: reduceAddress(visitingAddress),
      shippingAddress: hasShipping && shippingAddress ? reduceAddress(shippingAddress) : undefined,
      lowestGradeCd,
      highestGradeCd,
      aiCode: update.aiCode,
      aiLevelCd: update.aiLevelCd,
      aiStatusCd,
      orgSubTypeCd,
      orgTypeCd,
      closureDate,
      submitterComment,
      deactivateDisclaimer,
      aiCategoryCd,
      fax:   fax ? {
        formatted: fax
      } : undefined
    };
  };

  const getMeta = (): Partial<IRequestMeta> => {
    return {
      creator: userName,
      creatorEmail: userEmail,
      disclaimer,
      submitter
    };
  };

  const { errors, message: errorMessage } = error;

  const getErrorMessages = (field: string): string[] => {
    const fieldError = getError(field);
    return fieldError._errors as string[];
  };

  const getError = (field: string): IFieldErrors => {
    const empty = { _errors: [] };
    const error = errors ? get(errors, field, empty) : empty;
    if ('_errors' in error && Array.isArray(error._errors)) {
      return error;
    }
    return empty;
  };

  const hasErrors = (field: string): boolean => {
    return getErrorMessages(field).length > 0 ? true : false;
  };

  const getChange = (field: string): any => {
    const updated = changes || {};
    return isChanged(field) ? get(updated, field) : null;
  };

  const getCurrent = (field: string): any => {
    return get(original || {}, field);
  };

  const isSet = (field: string): boolean => {
    return isFieldSet(field, original || {});
  };

  const isChanged = (field: string): boolean => {
    let updates: any = changes || {};
    return isFieldSet(field, updates);
  };

  const getChangeColor = (field: string): 'cb-removed' | 'cb-modified' | 'cb-added' | undefined => {
    const wasSet = isSet(field);
    if (!isChanged(field)) {
      return undefined;
    }

    const change = getChange(field);
    if (change === undefined) {
      return 'cb-removed';
    } else if (!wasSet && change !== undefined) {
      return 'cb-added';
    } else {
      return 'cb-modified';
    }
  };
  const onFieldChange = () => { };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (validating)
      validate();
  }, [
    name,
    email,
    phone,
    website,
    visitingAddress,
    hasShipping,
    shippingAddress,
    aiStatusCd,
    closureDate,
    isMerge,
    mergeAICd,
    mergeName,
    mergeAddress,
    lowestGradeCd,
    highestGradeCd,
    orgSubTypeCd,
    disclaimer,
    submitter,
    deactivateDisclaimer,
    aiCategoryCd,
    fax
  ]);

  const validate = () => {
    try {
      const pending = getData();
      const meta = getMeta();
      orgService.updateRequest.validate(pending, meta, initial);
      setError({
        message: '',
        errors: { _errors: [] }
      });
    } catch (error: any) {
      if (error instanceof ValidationError) {
        setError({
          message: error.message,
          errors: error.fieldErrors
        });
        console.log(error.fieldErrors);
      }
    }
  };

  const submit = async () => {
    try {
      const pending = getData();
      const meta = getMeta();
      return await orgService.updateRequest.create(pending, meta, initial);
    } catch (error: any) {

      if (error instanceof ValidationError) {
        setError({
          message: error.message,
          errors: error.fieldErrors
        });
      } else {
        console.error(error);
        setError({
          message: "Unknown error occurred while saving update request.",
          errors: { _errors: [error.message] }
        });
      }
      window.scrollTo(0, 0);
      throw error;
    }
  };

  const handleCancel = (e: any) => {
    if (onCancel) onCancel();
  };

  const handleSubmit = (e: any) => {
    setValidating(true);
    setSubmitting(true);
    submit().then((data) => {
      if (onComplete) onComplete(data);
    }, (error) => { }).finally(() => {
      setSubmitting(false);
    });
  };

  const fieldMap = [
    { key: '', name: '' },
    { key: 'diff', name: "Not Updated" },
    { key: 'meta.creator', name: 'Creator' },
    { key: 'meta.creatorEmail', name: 'Creator Email' }
  ];

  return <form onSubmit={handleSubmit}>
    {errorMessage &&
      <div className="cb-notification" aria-labelledby="apricot_notification_542" role="region" style={{ marginBottom: '20px' }}>
        <div className="cb-notification-container">
          <div className="cb-notification-header">
            <i className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
            <h4 id="apricot_notification_542" className="cb-notification-title"><strong>Errors Occurred on the Update</strong></h4>
          </div>
          <div className="cb-notification-content">
            {errorMessage}
            <div style={{ whiteSpace: 'pre-line' }}>
              {fieldMap.map((field, i) => { return hasErrors(field.key) ? <li key={`update-field-error-${i}`}>{field.name ? `${field.name}:` : ``} {getErrorMessages(field.key).join("\n")}</li> : ''; })}
            </div>
          </div>
        </div>
      </div>
    }
    <StyledGrid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FieldRow>
            <Typography variant="body1">Name</Typography>
            {(readOnly && isChanged('name')) &&
              <div><i className={getChangeColor('name')}>Current: {getCurrent('name') || '(empty)'}</i></div>
            }
            <Input
              className={readOnly ? getChangeColor('name') : ''}
              disabled={readOnly}
              validation={hasErrors('data.name') ? 'error' : undefined}
              validationMsg={getErrorMessages('data.name').join("\n")}
              onChange={(e: any) => { setName(e.target.value); onFieldChange(); }}
              value={name} />
          </FieldRow>

          <FieldRow>
            <Typography variant="body1">AI Status</Typography>
            {(readOnly && isChanged('aiStatusCd')) &&
              <div><i className={getChangeColor('aiStatusCd')}>Current: {isAIActive(getCurrent('aiStatusCd')) ? 'Active' : 'Inactive'}</i></div>
            }
            <Dropdown disabled={readOnly}
              className={readOnly ? getChangeColor('aiStatusCd') : ''}
              values={getAIStatusOptions()}
              onChange={(value: string) => {
                setStatusCd(value === '1' ? '1' : '2');
                if (isAIActive(value)) {
                  setClosureDate(undefined);
                  setDeactivateDisclaimer(undefined);
                }
                onFieldChange();
              }} value={aiStatusCd}></Dropdown>
          </FieldRow>
          {!isAIActive(aiStatusCd || '') &&
            <FieldRow>
              <FieldRow>
                {readOnly ?
                  <div>
                    <div className={readOnly ? getChangeColor('closureDate') : ''}>Closure Date: {closureDate}</div>
                    {(isChanged('closureDate')) &&
                      <div><i className={getChangeColor('closureDate')}>Current: {getCurrent('closureDate') || '(empty)'}</i></div>
                    }
                  </div> :
                  <div>
                    <Typography variant="body1">Closure Date</Typography>
                    <DatePicker onSelect={(event) => {
                      if (event && event.dateObj && event.dateObj.start) {
                        const select: Date = new Date(event.dateObj.start);
                        const date = `${select.getMonth() + 1}/${select.getDate()}/${select.getFullYear()}`;
                        setClosureDate(date);
                      } else {
                        setClosureDate(undefined);
                      }
                    }} singleDate startDate={closureDate} />
                    {hasErrors('data.closureDate') &&
                      <span className='cb-input-helper cb-validation-error'>{getErrorMessages('data.closureDate').join("\n")}</span>
                    }
                  </div>
                }
              </FieldRow>
              <FieldRow>
                <Checkbox disabled={readOnly} label="Are you merging with another school?" style={{ display: 'inline-block' }} checked={isMerge} onChange={() => {
                  if (isMerge) {
                    setMerge(false);
                  } else {
                    setMerge(true);
                    if (!mergeAddress) {
                      setMergeAddress({
                        countryIsoCd: visitingAddress.countryIsoCd,
                        stateCd: visitingAddress.stateCd,
                        province: visitingAddress.province
                      });
                    }
                  }
                  onFieldChange();
                }} />
              </FieldRow>
              {isMerge &&
                <Grid>
                  <FieldRow>
                    <strong>Merging into Following School:</strong>
                  </FieldRow>
                  <FieldRow>
                    <Typography variant="body1">AI Code:</Typography>
                    {(readOnly && isChanged('merge.aiCode')) &&
                      <div><i className={getChangeColor('merge.aiCode')}>Current: {getCurrent('merge.aiCode') || '(empty)'}</i></div>
                    }
                    <Input className={readOnly ? getChangeColor('merge.aiCode') : ''} disabled={readOnly} validation={hasErrors('data.merge') || hasErrors('data.merge.aiCode') ? 'error' : undefined} validationMsg={getErrorMessages('data.merge').concat(getErrorMessages('data.merge.aiCode')).join("\n")} onChange={(e: any) => { setMergeAICd(e.target.value); onFieldChange(); }} value={mergeAICd} />
                  </FieldRow>
                  <FieldRow>
                    <strong>OR</strong>
                  </FieldRow>
                  <FieldRow>
                    <Typography variant="body1">Name:</Typography>
                    {(readOnly && isChanged('merge.name')) &&
                      <div><i className={getChangeColor('merge.name')}>Current: {getCurrent('merge.name') || '(empty)'}</i></div>
                    }
                    <Input className={readOnly ? getChangeColor('merge.name') : ''} disabled={readOnly} validation={hasErrors('data.merge') || hasErrors('data.merge.name') ? 'error' : undefined} validationMsg={getErrorMessages('data.merge').concat(getErrorMessages('data.merge.name')).join("\n")} onChange={(e: any) => { setMergeName(e.target.value); onFieldChange(); }} value={mergeName} />
                  </FieldRow>
                  <AddressFields readOnly={readOnly} showDiff={readOnly} original={getCurrent('merge.address') || {}} errors={getError('data.merge.address')} onUpdate={(address) => { setMergeAddress(address); onFieldChange(); }} address={mergeAddress} />
                </Grid>
              }
              <FieldRow>
                <h6 style={{ marginBottom: "10px" }}>Disclaimer</h6>
                <Typography variant="body1" style={{ marginBottom: '5px' }}>I understand by submitting this request that the College Board code for my organization will be deactivated on the date indicated. I understand that once deactivated, my organization will no longer have access to order College Board assessments nor be able to access, view, or extract student data, including score data. I confirm that my organization has extracted all score and student data from College Board before submitting this form.</Typography>
                <Checkbox disabled={readOnly} label='Deactivate' style={{ display: 'inline-block' }} checked={deactivateDisclaimer} onChange={() => {
                  if (!deactivateDisclaimer) {
                    setDeactivateDisclaimer(true);
                  } else {
                    setDeactivateDisclaimer(undefined);
                  }
                  onFieldChange();
                }} />
                {hasErrors('data.deactivateDisclaimer') &&
                  <span className='cb-input-helper cb-validation-error'>You must agree to the disclaimer</span>
                }
              </FieldRow>
            </FieldRow>
          }
        </Grid>
        <Grid item xs={6}>
          <FieldData>AI Code Level: <FieldValue>{update.aiLevelCd}</FieldValue></FieldData>
          <FieldData>AI Code: <FieldValue>{update.aiCode}</FieldValue></FieldData>
        </Grid>
      </Grid>
    </StyledGrid>

    <StyledGrid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FieldRow><strong>Visiting Address</strong></FieldRow>
          <AddressFields readOnly={readOnly} showDiff={readOnly} errors={getError('data.visitingAddress')} address={visitingAddress} original={getCurrent('visitingAddress') || {}} onUpdate={(address) => { setVisitingAddress(address); onFieldChange(); }} />
        </Grid>
        <Grid item xs={6}>
          <FieldRow><Checkbox disabled={readOnly} label={<strong>Shipping Address</strong>} style={{ display: 'inline-block' }} checked={hasShipping} onChange={() => {
            if (!hasShipping) {
              setShipping(true);
              if (!shippingAddress) {
                setShippingAddress({
                  countryIsoCd: visitingAddress.countryIsoCd,
                  stateCd: visitingAddress.stateCd,
                  province: visitingAddress.province
                });
              }
            } else {
              setShipping(false);
            }
            onFieldChange();
          }} /></FieldRow>
          <Typography variant="body1" style={{ fontWeight: 500, color: "#ff0000" }}>College Board assessment materials must be shipped, stored, and administered at the same location. Adding an alternate shipping address is a request for an exception to this policy which requires additional processing time. Please add details to support this exception request in the comment field below.</Typography>
          {hasShipping &&
            <AddressFields
              additionalInfo={true}
              customLabels={{ label: 'Alternate Shipping Institution Name', contactName: 'Shipping Contact Name', contactPhone: 'Shipping Contact Phone' }}
              readOnly={readOnly}
              showDiff={readOnly}
              original={getCurrent('shippingAddress') || {}}
              errors={getError('data.shippingAddress')}
              address={shippingAddress}
              onUpdate={(address) => { setShippingAddress(address); onFieldChange(); }} />
          }
        </Grid>
      </Grid>
    </StyledGrid>

    <StyledGrid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FieldRow>
            <Typography variant="body1">General Institution Website</Typography>
            {(readOnly && isChanged('website')) &&
              <div><i className={getChangeColor('website')}>Current: {getCurrent('website') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={readOnly ? getChangeColor('website') : ''}
              validation={hasErrors('data.website') ? 'error' : undefined}
              validationMsg={getErrorMessages('data.website').join("\n")}
              onChange={(e: any) => { setWebsite(e.target.value); onFieldChange(); }} value={website} />
          </FieldRow>

          <FieldRow>
            {(readOnly && isChanged('phone.formatted')) &&
              <div><i className={getChangeColor('phone.formatted')}>Current: {getCurrent('phone.formatted') || '(empty)'}</i></div>
            }
            <InputPhoneInt
              disabled={readOnly}
              label='General Institution Phone'
              className={readOnly ? getChangeColor('phone.formatted') : ''}
              validation={hasErrors('data.phone.formatted') ? 'error' : undefined}
              validationMsg={getErrorMessages('data.phone.formatted').join("\n")}
              defaultCountry={defaultCountry} defaultValue={phone} onChange={(e) => { setPhone(e.target.value); onFieldChange(); }} />
          </FieldRow>

          <FieldRow>
            <Typography variant="body1">General Institution Fax</Typography>
            {(readOnly && isChanged('fax.formatted')) &&
                <div><i className={getChangeColor('fax.formatted')}>Current: {getCurrent('fax.formatted') || '(empty)'}</i></div>
            }
            <Input
                disabled={readOnly}
                className={readOnly ? getChangeColor('fax.formatted') : ''}
                validation={hasErrors('data.fax.formatted') ? 'error' : undefined}
                validationMsg={getErrorMessages('data.fax.formatted').join("\n")}
                onChange={(e: any) => { setFax(e.target.value); onFieldChange(); }} value={fax} />
          </FieldRow>

          <FieldRow>
            <Typography variant="body1">General Institution Email</Typography>
            {(readOnly && isChanged('email')) &&
              <div><i className={getChangeColor('email')}>Current: {getCurrent('email') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={readOnly ? getChangeColor('email') : ''}
              validation={hasErrors('data.email') ? 'error' : undefined}
              validationMsg={getErrorMessages('data.email').join("\n")}
              onChange={(e: any) => { setEmail(e.target.value); onFieldChange(); }} value={email} />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          {hasErrors('data.orgTypeCd') &&
            <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>
              Org Type is missing from the request and couldn't be retrieved from database.  This record is likely bad and will fail to submit, if you need this fixed please contact support.
            </span>
          }
          <FieldRow>
            <Typography variant="body1">School Type</Typography>
            {(readOnly && isChanged('orgSubTypeCd')) &&
              <div><i className={getChangeColor('orgSubTypeCd')}>Current: {getCurrent('orgSubTypeCd') ? getOrgSubTypeLabel(getCurrent('orgSubTypeCd')) : '(empty)'}</i></div>
            }
            <Dropdown className={readOnly ? getChangeColor('orgSubTypeCd') : ''} disabled={true}
              values={getOrgSubTypeOptions()}
              onChange={(value) => { setOrgSubTypeCd(value); onFieldChange(); }} value={orgSubTypeCd?.toString()}></Dropdown>
          </FieldRow>
          {(gradeRequired) &&
            <>
              <FieldRow>
                <Typography variant="body1">Lowest Grade Offered</Typography>
                {(readOnly && isChanged('lowestGradeCd')) &&
                  <div><i className={getChangeColor('lowestGradeCd')}>Current: {getCurrent('lowestGradeCd') ? getGradeName(getCurrent('lowestGradeCd')) : '(empty)'}</i></div>
                }
                <Dropdown className={readOnly ? getChangeColor('lowestGradeCd') : ''} disabled={readOnly}
                  validation={hasErrors('data.lowestGradeCd') ? 'error' : undefined}
                  validationMsg={getErrorMessages('data.lowestGradeCd').join("\n")}
                  values={getGradeOptions()}
                  onChange={(value) => { setLowestGradeCd(value); onFieldChange(); }}
                  value={lowestGradeCd}></Dropdown>
              </FieldRow>

              <FieldRow>
                <Typography variant="body1">Highest Grade Offered</Typography>
                {(readOnly && isChanged('highestGradeCd')) &&
                  <div><i className={getChangeColor('highestGradeCd')}>Current: {getCurrent('highestGradeCd') ? getGradeName(getCurrent('highestGradeCd')) : '(empty)'}</i></div>
                }
                <Dropdown className={readOnly ? getChangeColor('highestGradeCd') : ''} disabled={readOnly}
                  validation={hasErrors('data.highestGradeCd') ? 'error' : undefined}
                  validationMsg={getErrorMessages('data.highestGradeCd').join("\n")}
                  values={getGradeOptions()}
                  onChange={(value) => { setHighestGradeCd(value); onFieldChange(); }}
                  value={highestGradeCd}></Dropdown>
              </FieldRow>
            </>
          }

        </Grid>

        <Grid item xs={12}>
          <FieldRow>
            <Typography variant="body1">Additional Comments</Typography>
            {(readOnly && isChanged('submitterComment')) &&
              <div><i className={getChangeColor('submitterComment')}>Current: {getCurrent('submitterComment') || '(empty)'}</i></div>
            }
            <TextArea
              disabled={readOnly}
              className={readOnly ? getChangeColor('submitterComment') : ''}
              validation={hasErrors('data.submitterComment') ? 'error' : undefined}
              validationMsg={getErrorMessages('data.submitterComment').join("\n")}
              onChange={(e: any) => { setSubmitterComment(e.target.value); onFieldChange(); }} value={submitterComment} />
          </FieldRow>
        </Grid>
      </Grid>
    </StyledGrid>

    <StyledGrid style={{ marginBottom: "30px" }}>
      <h4 style={{ marginBottom: "10px" }}>Disclaimer</h4>
      <FieldRow>
        <Typography variant="body1" style={{ marginBottom: '5px' }}>By submitting this request I confirm that all of the information provided is true and accurately describes the organization named on this form and have the authority to make this request. I understand that if any of the information is false, deactivation of the school code and/or legal action may result.</Typography>
        <Checkbox disabled={readOnly} label='Accept' style={{ display: 'inline-block' }} checked={disclaimer} onChange={() => {
          if (!disclaimer) {
            setDisclaimer(true);
          } else {
            setDisclaimer(false);
          }
          onFieldChange();
        }} />
        {hasErrors('meta.disclaimer') &&
          <span className='cb-input-helper cb-validation-error'>You must agree to the disclaimer</span>
        }
      </FieldRow>

      <FieldRow>
        <Typography variant="body1">Submitter</Typography>
        <Input
          disabled={readOnly}
          validation={hasErrors('meta.submitter') ? 'error' : undefined}
          validationMsg={getErrorMessages('meta.submitter').join("\n")}
          onChange={(e: any) => { setSubmitter(e.target.value); onFieldChange(); }} value={submitter} />
      </FieldRow>
    </StyledGrid>

    {!readOnly &&
      <PrimaryButton style={{ marginRight: '10px' }} onClick={handleSubmit} disabled={submitting}>Submit</PrimaryButton>
    }
    <Button onClick={handleCancel} disabled={submitting}>{readOnly ? 'Close' : 'Cancel'}</Button>
  </form>;
};
